import React, { useEffect, useState, FC } from 'react';
import styles from './custom-price.module.scss';
import { PUBLIC_PATH } from '../../../../../shared/config/routes-config';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { trackEvent } from '../../../../../shared/services/utils/tracking/tracking.service';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import type { CustomPriceModel } from '../../../../../shared/ui-models/unit-type.ui-model';
import { UserRole } from '../../../../../shared/enums/user-role.enum';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { AddCustomPriceModal } from '../add-custom-price-modal/add-custom-price-modal.component';
import DeleteIcon from '@material-ui/icons/Delete';
import Tooltip from '../../../../../ui/Tooltip';
import {
  InputText,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import { ApplyToOtherUnitsModal } from '../apply-to-other-units-modal/apply-to-other-units-modal.component';
import { AdjustCustomPriceModal } from '../adjust-custom-price-modal/adjust-custom-price-modal.component';
import { MagicIcon } from '../../../../../ui/Icons';

export interface CustomPriceProps {
  data: {
    customPrice: CustomPriceModel[];
    basePrice: number;
    basePriceInUSD?: number;
    currentUnitTypeId?: string;
    currentPropertyId?: string;
  };
  config: {
    userRole?: UserRole;
    userRoles?: UserRole[];
  };
  eventHandlers: {
    updateCustomPrice: (customPrices: CustomPriceModel[]) => void;
  };
}
export const CustomPrice: FC<CustomPriceProps> = ({ data, config, eventHandlers }) => {
  const { userRole, userRoles } = config || {};
  const isMobileView = useWindowResizer();
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState<boolean>(false);
  const [showPriceUpdateConfirm, setShowPriceUpdateConfirm] = useState<boolean>(false);
  const [hasPendingPriceUpdates, setHasPendingPriceUpdates] = useState<boolean>(false);
  const [customPrices, setCustomPrices] = useState<CustomPriceModel[]>([]);
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());
  const [showApplyModal, setShowApplyModal] = useState<boolean>(false);
  const [showAdjustModal, setShowAdjustModal] = useState<boolean>(false);

  const isPermited = (permittedList: UserRole[]) => {
    return (
      (userRole && permittedList.indexOf(userRole) > -1) ||
      permittedList.some((item) => userRoles?.includes(item))
    );
  };

  const pricingManagerRoles = [
    UserRole.SUPER_ADMIN,
    UserRole.ADMIN,
    UserRole.ECOMMERCE_SENIOR_SPECIALIST,
    UserRole.ECOMMERCE_SPECIALIST
  ];

  const canApplyToOtherUnits = isPermited(pricingManagerRoles);
  const canAdjustPrices = isPermited(pricingManagerRoles);

  useEffect(() => {
    setCustomPrices(data?.customPrice);
    setSelectedIndexes(new Set());
    setHasPendingPriceUpdates(false);
    setShowPriceUpdateConfirm(false);
    setShowAdjustModal(false);
  }, [data?.customPrice]);

  const toggleRowSelection = (index: number) => {
    setSelectedIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIndexes((prev) => {
      if (customPrices.length === 0) return prev;

      if (prev.size === customPrices.length) {
        return new Set();
      }

      return new Set(customPrices.map((_, index) => index));
    });
  };

  const deleteCustomPrice = (index: number) => {
    const newCustomPrice = customPrices.filter((_, itemIndex) => itemIndex !== index);
    setCustomPrices(newCustomPrice);
    eventHandlers.updateCustomPrice(newCustomPrice);
    setHasPendingPriceUpdates(false);

    setSelectedIndexes((prev) => {
      if (prev.size === 0) return prev;

      const next = new Set<number>();
      prev.forEach((selectedIndex) => {
        if (selectedIndex === index) return;
        if (selectedIndex > index) {
          next.add(selectedIndex - 1);
        } else {
          next.add(selectedIndex);
        }
      });
      return next;
    });
  };

  const deleteSelectedCustomPrices = () => {
    if (selectedIndexes.size === 0) return;

    const newCustomPrice = customPrices.filter((_, idx) => !selectedIndexes.has(idx));
    setCustomPrices(newCustomPrice);
    eventHandlers.updateCustomPrice(newCustomPrice);
    setHasPendingPriceUpdates(false);
    setSelectedIndexes(new Set());
  };

  /**
   * Converts selected dates into periods and sets editable USD price.
   */
  function convertDatesToPeriods(dateStrings: string[], defaultPriceInUSD = '0') {
    const periods = [];

    for (let i = 0; i < dateStrings.length; i += 2) {
      if (i + 1 < dateStrings.length) {
        periods.push({
          from: new Date(dateStrings[i]),
          to: new Date(dateStrings[i + 1]),
          price: '',
          priceInUSD: defaultPriceInUSD
        });
      }
    }

    return periods;
  }

  const getIsRowSelected = (index: number) => selectedIndexes.has(index);

  const updatePriceInUSD = (index: number, priceInUSD: string) => {
    const newCustomPrice = customPrices.map((item, itemIndex) =>
      itemIndex === index ? { ...item, priceInUSD } : item
    );
    setCustomPrices(newCustomPrice);
    setHasPendingPriceUpdates(true);
  };

  const applyPercentageAdjustment = (adjustedCustomPrices: CustomPriceModel[]) => {
    setCustomPrices(adjustedCustomPrices);
    eventHandlers.updateCustomPrice(adjustedCustomPrices);
    setHasPendingPriceUpdates(false);
    setSelectedIndexes(new Set());
  };

  const savePriceUpdates = () => {
    if (!hasPendingPriceUpdates) return;
    eventHandlers.updateCustomPrice(customPrices);
    setHasPendingPriceUpdates(false);
    setShowPriceUpdateConfirm(false);
  };

  return (
    <div className="flex-1 mt-4 pb-8 rounded-lg p-5 bg-neutral-0">
      {customPrices?.length > 0 ? (
        <>
          <div className="font-semibold font-gilroy-medium text-primary-500 text-3xl mb-4 flex justify-between items-center">
            Dynamic Price
            <div className="flex items-center gap-3">
              <Button
                onClick={() => {
                  if (hasPendingPriceUpdates) setShowPriceUpdateConfirm(true);
                }}
                size={isMobileView ? ButtonSize.full : ButtonSize.medium}
                disabled={!hasPendingPriceUpdates}
              >
                Save Update
              </Button>
              <Button
                onClick={() => {
                  if (selectedIndexes.size > 0) setShowBulkDeleteConfirm(true);
                }}
                type={ButtonType.danger}
                size={isMobileView ? ButtonSize.full : ButtonSize.medium}
                disabled={selectedIndexes.size === 0}
              >
                Delete ({selectedIndexes.size})
              </Button>
              <Button
                onClick={() => {
                  setShowAddModal(true);
                  trackEvent('record_saved', {
                    entity: 'unit_type',
                    action: 'create',
                    outcome: 'success'
                  });
                }}
                tracking={{
                  event: 'record_saved',
                  entity: 'custom_price',
                  action: 'create',
                  outcome: 'success'
                }}
                size={isMobileView ? ButtonSize.full : ButtonSize.medium}
              >
                Add
              </Button>
              {canAdjustPrices && (
                <Tooltip title="Increase or decrease prices by a percentage" placement="top">
                  <Button
                    onClick={() => setShowAdjustModal(true)}
                    type={ButtonType.primaryInverted}
                    size={isMobileView ? ButtonSize.full : ButtonSize.medium}
                  >
                    Adjust %
                  </Button>
                </Tooltip>
              )}
              {canApplyToOtherUnits && data.currentUnitTypeId && customPrices.length > 0 && (
                <Tooltip title="Apply custom prices to other units" placement="top">
                  <button
                    type="button"
                    aria-label="Apply custom prices to other units"
                    className="flex items-center justify-center w-[4rem] h-[4rem] rounded-md border border-gray-200 bg-gray-50 hover:bg-amber-50 hover:border-amber-300 text-gray-400 hover:text-amber-600 transition-colors cursor-pointer"
                    onClick={() => setShowApplyModal(true)}
                  >
                    <MagicIcon size="20px" />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            <TableContainer component={Paper}>
              <Table aria-label="table">
                <TableHead>
                  <TableRow>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Price in USD</TableCell>
                    <TableCell>
                      <Button
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleSelectAll();
                        }}
                        type={ButtonType.primaryInverted}
                        size={ButtonSize.small}
                      >
                        {selectedIndexes.size === customPrices.length ? 'Clear All' : 'Select all'}
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customPrices.map((item, index) => {
                    const isSelected = getIsRowSelected(index);

                    return (
                      <TableRow
                        key={`${item.from}-${item.to}-${index}`}
                        className="cursor-pointer hover:bg-primary-50"
                        onClick={() => toggleRowSelection(index)}
                      >
                        <TableCell className={isSelected ? 'bg-primary-50' : undefined}>
                          {new Date(Number(item.from)).toDateString()}
                        </TableCell>

                        <TableCell className={isSelected ? 'bg-primary-50' : undefined}>
                          {new Date(Number(item.to)).toDateString()}
                        </TableCell>
                        <TableCell className={isSelected ? 'bg-primary-50' : undefined}>
                          {item.price || '-'}
                        </TableCell>
                        <TableCell
                          className={isSelected ? 'bg-primary-50' : undefined}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <InputText
                            data={{
                              value: item.priceInUSD ?? ''
                            }}
                            config={{
                              maxRows: 1,
                              type: InputTextType.number
                            }}
                            eventHandlers={{
                              onChangeHandler: (value: string) => {
                                updatePriceInUSD(index, value);
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <DeleteIcon
                            onClick={(event) => {
                              event.stopPropagation();
                              deleteCustomPrice(index);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </>
      ) : (
        <div>
          <div className="font-semibold font-gilroy-medium text-primary-500 text-3xl mb-4">
            Dynamic Price
          </div>
          <img
            src={`${PUBLIC_PATH}/assets/pngs/add-custom-price.png`}
            alt="add custom price"
            className="block m-auto max-w-full"
          />

          <Button
            onClick={() => {
              setShowAddModal(true);
              trackEvent('record_saved', {
                entity: 'unit_type',
                action: 'create',
                outcome: 'success'
              });
            }}
            tracking={{
              event: 'record_saved',
              entity: 'custom_price',
              action: 'create',
              outcome: 'success'
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            {' '}
            Add Dynamic Price
          </Button>
          {canAdjustPrices && (
            <div className="mt-3">
              <Button
                onClick={() => setShowAdjustModal(true)}
                type={ButtonType.primaryInverted}
                size={isMobileView ? ButtonSize.full : ButtonSize.medium}
              >
                Adjust % from Base Price
              </Button>
            </div>
          )}
        </div>
      )}
      {canAdjustPrices && (
        <AdjustCustomPriceModal
          data={{
            customPrices,
            basePriceInUSD: data.basePriceInUSD || 0,
            basePrice: data.basePrice
          }}
          config={{ showModal: showAdjustModal }}
          eventHandlers={{
            closeHandler: () => setShowAdjustModal(false),
            applyAdjustment: applyPercentageAdjustment
          }}
        />
      )}

      <AddCustomPriceModal
        data={{ customPrice: customPrices, basePrice: data.basePrice }}
        config={{
          showModal: showAddModal
        }}
        eventHandlers={{
          denyAction: () => {
            setShowAddModal(false);
          },
          confirmAction: () => {
            setShowAddModal(false);
          },
          addCustomPrice: (date, priceInUSD) => {
            eventHandlers.updateCustomPrice([
              ...customPrices,
              ...convertDatesToPeriods(date, priceInUSD)
            ]);
            setHasPendingPriceUpdates(false);
          }
        }}
      />

      <Modal
        config={{ showModal: showPriceUpdateConfirm, fullScreen: false, showClose: false }}
        eventHandlers={{
          closeModalHandler: () => setShowPriceUpdateConfirm(false)
        }}
      >
        <div className="flex flex-col justify-center mx-[10%] min-h-[30vh]">
          <div className="font-nunito font-bold text-[#37454d] text-left text-[1.8rem]">
            Confirmation
          </div>
          <div className="mt-2 font-nunito text-[#37454d] text-left leading-normal text-[1.8rem] mb-5">
            <p>Are you sure you want to update USD price changes?</p>
          </div>
          <div className="flex flex-row justify-end mt-8">
            <div className="mr-4">
              <Button
                onClick={() => setShowPriceUpdateConfirm(false)}
                type={ButtonType.primaryInverted}
                size={isMobileView ? ButtonSize.full : ButtonSize.medium}
              >
                No
              </Button>
            </div>
            <Button
              onClick={savePriceUpdates}
              size={isMobileView ? ButtonSize.full : ButtonSize.medium}
            >
              Yes
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        config={{ showModal: showBulkDeleteConfirm, fullScreen: false, showClose: false }}
        eventHandlers={{
          closeModalHandler: () => setShowBulkDeleteConfirm(false)
        }}
      >
        <div className="flex flex-col justify-center mx-[10%] min-h-[30vh]">
          <div className="font-nunito font-bold text-[#37454d] text-left text-[1.8rem]">
            Confirmation
          </div>
          <div className="mt-2 font-nunito text-[#37454d] text-left leading-normal text-[1.8rem] mb-5">
            <p>Are you sure you want to delete {selectedIndexes.size} custom price(s)?</p>
          </div>
          <div className="flex flex-row justify-end mt-8">
            <div className="mr-4">
              <Button
                onClick={() => setShowBulkDeleteConfirm(false)}
                type={ButtonType.primaryInverted}
                size={isMobileView ? ButtonSize.full : ButtonSize.medium}
              >
                No
              </Button>
            </div>
            <Button
              onClick={() => {
                deleteSelectedCustomPrices();
                setShowBulkDeleteConfirm(false);
              }}
              type={ButtonType.danger}
              size={isMobileView ? ButtonSize.full : ButtonSize.medium}
            >
              Yes
            </Button>
          </div>
        </div>
      </Modal>

      {showApplyModal && data.currentUnitTypeId && (
        <ApplyToOtherUnitsModal
          config={{
            showModal: true,
            currentUnitTypeId: data.currentUnitTypeId,
            currentPropertyId: data.currentPropertyId
          }}
          data={{
            dataType: 'customPrices',
            dataToApply: { customPrices },
            dataPreview: `${customPrices.length} custom price${customPrices.length > 1 ? 's' : ''}`
          }}
          eventHandlers={{
            closeHandler: () => setShowApplyModal(false)
          }}
        />
      )}
    </div>
  );
};
